import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Avatar, Modal, Input, Select, message } from 'antd';
import * as Yup from 'yup';

import { UpdateCoverImageContainer, UpdateFormContainer } from './styles';
import {
	RssFeedCategoriesEnum,
	RssFeedCategoriesTranslationsEnum,
} from '../../../../enums/rss-feed-categories';
import {
	RssFeedRagionsEnum,
	RssFeedRagionsTranslationsEnum,
} from '../../../../enums/rss-feed-regions';
import { TbEdit } from 'react-icons/tb';
import { resolveFileSrc } from '../../../../helpers/fileSrcResolver';
import env from '../../../../config/env';
import Form from '../../../../components/Form';
import RssFeedsApi from '../../../../services/sdks/rss-feeds';

const DEFAULT_DATA = {
	title: '',
	url: '',
	category: undefined,
	region: undefined,
	coverImage: null,
};

const validationSchema = Yup.object().shape({
	title: Yup.string().required(),
	url: Yup.string().required(),
	category: Yup.string().oneOf(Object.values(RssFeedCategoriesEnum)).required(),
	region: Yup.string().oneOf(Object.values(RssFeedRagionsEnum)).nullable().default(null),
});

export const UpsertRssFeedModal = ({ isVisible, initialData, onSubmit, onCancel }) => {
	const uploadInputRef = useRef(null);
	const [isLoading, setIsLoading] = useState(false);
	const [coverImageFile, setCoverImageFile] = useState(null);
	const [formData, setFormData] = useState(initialData ?? DEFAULT_DATA);

	const action = useMemo(() => {
		return initialData ? 'UPDATE' : 'CREATE';
	}, [initialData]);

	const isFormValid = useMemo(() => {
		try {
			validationSchema.validateSync(formData);
			return true;
		} catch (error) {
			return false;
		}
	}, [formData]);

	const coverImageSrc = useMemo(() => {
		if (coverImageFile) {
			return URL.createObjectURL(coverImageFile);
		}

		return initialData?.coverImage
			? resolveFileSrc({
					fileName: initialData?.coverImage,
					folder: ['profile_pics'],
					bucket: env.miscS3Bucket,
			  })
			: null;
	}, [coverImageFile, initialData?.coverImage]);

	useEffect(() => {
		setFormData(initialData ?? DEFAULT_DATA);
	}, [initialData]);

	const handleSubmit = useCallback(async () => {
		if (!isFormValid) {
			return;
		}

		setIsLoading(true);

		try {
			const payload = new FormData();
			payload.append('title', formData.title);
			payload.append('url', formData.url);
			payload.append('category', formData.category);
			payload.append('region', formData.region);

			if (coverImageFile) {
				payload.append('coverImage', coverImageFile);
			}

			let response = null;

			if (action === 'CREATE') {
				const { data } = await RssFeedsApi.createRssFeed(payload);
				response = data.rssFeed;
			} else {
				const { data } = await RssFeedsApi.updateRssFeed(initialData._id, payload);
				response = data.rssFeed;
			}

			await onSubmit({ action, data: response });
			message.success('Feed RSS atualizado com sucesso');
		} catch (error) {
			message.error('Ocorreu um erro, tente novamente');
		}

		setIsLoading(false);
		setCoverImageFile(null);
	}, [coverImageFile, formData, initialData, onSubmit, action, isFormValid]);

	const onSelectCoverImageFile = useCallback((e) => {
		const file = e.target.files[0];
		const ext = file?.name ? file.name.split('.')[1] : null;

		if (!file || !ext) {
			return setCoverImageFile(null);
		}

		if (!['jpg', 'jpeg', 'png'].includes(ext.toLowerCase())) {
			setCoverImageFile(null);
			return message.error(
				'Fromato inválido, selecione um imagem em um dos seguitnes formatos: .png, .jpg ou .jpeg',
				4
			);
		}

		return setCoverImageFile(file);
	}, []);

	if (!isVisible) {
		return null;
	}

	return (
		<>
			<input
				type='file'
				accept='.png, .jpg, .jpeg'
				style={{ display: 'none' }}
				onChange={onSelectCoverImageFile}
				ref={uploadInputRef}
			/>

			<Modal
				title={`${action === 'UPDATE' ? 'Atualizar' : 'Novo'} feed RSS`}
				okText='Salvar'
				visible={isVisible}
				onOk={handleSubmit}
				okButtonProps={{ loading: isLoading, icon: 'check-circle' }}
				cancelButtonProps={{ disabled: isLoading, icon: 'close-circle' }}
				onCancel={onCancel}>
				<UpdateFormContainer>
					<UpdateCoverImageContainer>
						<div className='overlay' onClick={() => uploadInputRef.current.click()}>
							<span>
								<TbEdit style={{ fontSize: 40 }} />
							</span>
						</div>
						<Avatar
							style={{ width: '120px', height: '120px', background: 'var(--primary)' }}
							src={coverImageSrc}>
							-
						</Avatar>
					</UpdateCoverImageContainer>

					<Form.Container>
						<Form.Item label='Título'>
							<Input
								size='large'
								placeholder='G1 Notícias, R7 News, CNN Brasil...'
								value={formData?.title}
								onChange={(e) => {
									e.persist();
									setFormData((prev) => ({ ...prev, title: e.target.value }));
								}}
							/>
						</Form.Item>

						<Form.Item label='Link'>
							<Input
								size='large'
								placeholder='https://g1.noticias/feed.xml'
								value={formData?.url}
								onChange={(e) => {
									e.persist();
									setFormData((prev) => ({ ...prev, url: e.target.value }));
								}}
							/>
						</Form.Item>

						<Form.Item label='Categoria'>
							<Select
								size='large'
								placeholder='Selecione a categoria'
								value={formData.category}
								onChange={(category) => setFormData((prev) => ({ ...prev, category }))}>
								{Object.values(RssFeedCategoriesEnum)
									.filter((c) => c !== 'CUSTOM')
									.map((category) => (
										<Select.Option value={category} key={category}>
											{RssFeedCategoriesTranslationsEnum[category]}
										</Select.Option>
									))}
							</Select>
						</Form.Item>

						<Form.Item label='Abrangência'>
							<Select
								size='large'
								placeholder='Selecione a região de abrangência'
								value={formData.region}
								onChange={(region) => setFormData((prev) => ({ ...prev, region }))}>
								{Object.values(RssFeedRagionsEnum).map((region) => (
									<Select.Option value={region} key={region}>
										{RssFeedRagionsTranslationsEnum[region]}
									</Select.Option>
								))}
							</Select>
						</Form.Item>
					</Form.Container>
				</UpdateFormContainer>
			</Modal>
		</>
	);
};
